// Screen-space HUD: top-left speed readout + right-edge 11-row leaderboard.
//
// Plain DOM (not Pixi) — CSS handles the glass styling and font rendering
// (architecture §11.2). Updated at ~4 Hz, not every frame.

import { tuning } from '@evox/shared';
import type { WorldSnapshot, PlayerId } from '@evox/shared';
import { theme } from './theme.js';

export interface Hud {
  update(snapshot: WorldSnapshot, localPlayerId: PlayerId): void;
  destroy(): void;
}

export function createHud(root: HTMLElement): Hud {
  const speedEl = document.createElement('div');
  speedEl.style.cssText = `position:absolute;top:18px;left:24px;font-size:56px;font-weight:700;color:${theme.text};font-family:system-ui,sans-serif;text-shadow:0 0 18px rgba(120,160,255,.25);pointer-events:none;`;
  root.appendChild(speedEl);

  // Fuel meter (player request 2026-06-12): a thin tank bar under the speed.
  const fuelWrap = document.createElement('div');
  fuelWrap.style.cssText = `position:absolute;top:88px;left:26px;width:120px;height:7px;border-radius:4px;background:rgba(140,160,200,.15);border:1px solid rgba(140,160,200,.25);pointer-events:none;`;
  const fuelFill = document.createElement('div');
  fuelFill.style.cssText = `height:100%;width:0%;border-radius:4px;background:linear-gradient(90deg,#ff7a2f,#ffb347);transition:width .25s;`;
  fuelWrap.appendChild(fuelFill);
  root.appendChild(fuelWrap);

  const boardEl = document.createElement('div');
  boardEl.style.cssText = `position:absolute;top:16px;right:16px;width:230px;background:rgba(10,14,26,.55);border:1px solid rgba(140,160,200,.18);border-radius:10px;padding:10px 12px;font-family:system-ui,sans-serif;font-size:13px;color:${theme.text};backdrop-filter:blur(6px);pointer-events:none;`;
  root.appendChild(boardEl);

  let lastUpdate = 0;

  return {
    update(snapshot, localPlayerId) {
      const now = performance.now();
      if (now - lastUpdate < 250) return; // ~4 Hz (GDD §12.2)
      lastUpdate = now;

      const sorted = [...snapshot.players].sort((a, b) => b.speed - a.speed);
      const me = sorted.findIndex((p) => p.id === localPlayerId);
      const local = me >= 0 ? sorted[me]! : null;

      speedEl.textContent = local ? local.speed.toFixed(1) : '';
      const fuelFrac = local ? Math.max(0, Math.min(1, local.fuel / tuning.maxFuel)) : 0;
      fuelFill.style.width = `${(fuelFrac * 100).toFixed(0)}%`;
      fuelWrap.style.opacity = fuelFrac > 0.01 ? '1' : '0.35';

      // Always 11 rows: top 10 + self as row 11 (GDD §12.2).
      const rows = sorted.slice(0, 10).map((p, i) => row(i + 1, p, p.id === localPlayerId));
      if (local) rows.push(row(me + 1, local, true, true));
      boardEl.innerHTML = rows.join('');
    },
    destroy() {
      speedEl.remove();
      boardEl.remove();
      fuelWrap.remove();
    },
  };
}

interface RowPlayer {
  name: string;
  color: string;
  speed: number;
}

function row(rank: number, p: RowPlayer, isLocal: boolean, anchor = false): string {
  const weight = isLocal ? 700 : 400;
  const border = anchor ? 'border-top:1px solid rgba(140,160,200,.25);margin-top:6px;padding-top:6px;' : '';
  const name = escapeHtml(p.name);
  return `<div style="display:flex;align-items:center;gap:8px;line-height:1.7;font-weight:${weight};${border}">
    <span style="width:18px;text-align:right;color:${theme.textDim}">${rank}</span>
    <span style="width:10px;height:10px;border-radius:50%;background:#${p.color};box-shadow:0 0 6px #${p.color}66"></span>
    <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${name}</span>
    <span style="font-variant-numeric:tabular-nums">${p.speed.toFixed(1)}</span>
  </div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
